import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus, Pencil, Trash2, Scissors, ToggleLeft, ToggleRight } from 'lucide-react';
import api from '../../lib/axios';
import PageHeader    from '../../components/ui/PageHeader';
import EmptyState    from '../../components/ui/EmptyState';
import ConfirmDialog from '../../components/ui/ConfirmDialog';
import ServiceEditModal from './ServiceEditModal';

function formatPrice(cents) {
  return (cents / 100).toLocaleString('en-US', { style: 'currency', currency: 'USD' });
}

function formatDuration(min) {
  if (min < 60) return `${min} min`;
  const h = Math.floor(min / 60), m = min % 60;
  return m ? `${h}h ${m}m` : `${h}h`;
}

export default function Services() {
  const qc = useQueryClient();
  const [editTarget, setEditTarget] = useState(null); // null=closed, {}=new, service=edit
  const [deleteTarget, setDeleteTarget] = useState(null);

  const { data, isLoading } = useQuery({
    queryKey: ['services'],
    queryFn:  () => api.get('/services').then(r => r.data.data),
  });

  const deleteMut = useMutation({
    mutationFn: (id) => api.delete(`/services/${id}`),
    onSuccess:  () => { qc.invalidateQueries(['services']); setDeleteTarget(null); },
  });

  const toggleMut = useMutation({
    mutationFn: (svc) => api.put(`/services/${svc.id}`, { ...svc, active: !svc.active }),
    onSuccess:  () => qc.invalidateQueries(['services']),
  });

  const services = data || [];

  return (
    <div className="p-4 sm:p-6 max-w-5xl">
      <PageHeader
        title="Services"
        subtitle={`${services.length} service${services.length !== 1 ? 's' : ''} configured`}
        action={
          <button onClick={() => setEditTarget({})} className="btn-primary">
            <Plus size={16} /> Add service
          </button>
        }
      />

      {isLoading ? (
        <div className="grid gap-3">
          {[1,2,3].map(i => <div key={i} className="card h-20 animate-pulse bg-surface-100" />)}
        </div>
      ) : services.length === 0 ? (
        <EmptyState
          icon={Scissors}
          title="No services yet"
          description="Add your first service to start accepting bookings."
          action={<button onClick={() => setEditTarget({})} className="btn-primary">Add service</button>}
        />
      ) : (
        <div className="card overflow-hidden">
          <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[480px]">
            <thead>
              <tr className="border-b border-surface-border bg-surface-50">
                <th className="text-left px-4 py-3 text-xs font-semibold text-ink-secondary uppercase tracking-wide">Service</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-ink-secondary uppercase tracking-wide hidden sm:table-cell">Category</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-ink-secondary uppercase tracking-wide">Duration</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-ink-secondary uppercase tracking-wide">Price</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-ink-secondary uppercase tracking-wide hidden md:table-cell">Staff</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-ink-secondary uppercase tracking-wide">Status</th>
                <th className="px-4 py-3 text-right text-xs font-semibold text-ink-secondary uppercase tracking-wide">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-surface-border">
              {services.map(svc => (
                <tr key={svc.id} className="hover:bg-surface-50 transition-colors">
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded-full shrink-0" style={{ background: svc.color }} />
                      <span className="font-medium text-ink whitespace-nowrap">{svc.name}</span>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-ink-secondary hidden sm:table-cell">{svc.category || '—'}</td>
                  <td className="px-4 py-3 text-ink-secondary whitespace-nowrap">{formatDuration(svc.duration_minutes)}</td>
                  <td className="px-4 py-3 font-medium text-ink whitespace-nowrap">{formatPrice(svc.price_cents)}</td>
                  <td className="px-4 py-3 hidden md:table-cell">
                    <div className="flex gap-1 flex-wrap">
                      {(svc.Staff || []).slice(0, 3).map(s => (
                        <span key={s.id} className="badge badge-blue">{s.name.split(' ')[0]}</span>
                      ))}
                      {(svc.Staff || []).length > 3 && (
                        <span className="badge badge-gray">+{svc.Staff.length - 3}</span>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <button onClick={() => toggleMut.mutate(svc)} className="text-ink-muted hover:text-ink transition-colors">
                      {svc.active
                        ? <ToggleRight size={22} className="text-emerald-500" />
                        : <ToggleLeft size={22} />
                      }
                    </button>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-1 justify-end">
                      <button onClick={() => setEditTarget(svc)} className="btn-ghost p-1.5 rounded-lg" title="Edit">
                        <Pencil size={15} />
                      </button>
                      <button onClick={() => setDeleteTarget(svc)} className="btn-ghost p-1.5 rounded-lg text-red-500 hover:bg-red-50" title="Delete">
                        <Trash2 size={15} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        </div>
      )}

      <ServiceEditModal
        open={editTarget !== null}
        service={editTarget}
        onClose={() => setEditTarget(null)}
      />

      <ConfirmDialog
        open={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={() => deleteMut.mutate(deleteTarget?.id)}
        loading={deleteMut.isPending}
        title="Delete service?"
        message={`"${deleteTarget?.name}" will be permanently deleted. Existing bookings are not affected.`}
      />
    </div>
  );
}
